// Phase 6 — Track B T-B3. The shared ApiClient.
//
// One method per endpoint Phase 6 routes consume. Two transport modes:
//
//   * 'cookie' — used by the Phase 6 web SPA. Sends `credentials: 'include'`
//     so the browser attaches the session cookie set by `POST /auth/login`.
//     The Express server is on the same origin (Vite dev proxies; prod
//     mounts the SPA at `/`), so no CORS dance is needed.
//
//   * 'bearer' — used by future mobile / CLI clients. Reads access tokens
//     from the supplied TokenStore and attaches them as `Authorization:
//     Bearer <token>` headers. Cookie credentials are NOT sent in this mode.
//
// Every non-2xx response throws a typed ApiError (or AuthRequiredError on
// 401). Network failures (DNS, offline, CORS) wrap into NetworkError. The
// web layer's react-query global onError pattern-matches on
// AuthRequiredError to redirect to /login; everything else surfaces a Toast.

import { endpoints, endpointPaths } from './endpoints.js';
import {
  ApiError,
  NetworkError,
  decodeError,
} from './errors.js';
import type { TokenStore } from './tokenStore.js';
import { NullTokenStore } from './tokenStore.js';
import type {
  AccountDeletionBody,
  AlertWithPinned,
  AnomalyList,
  CategoryList,
  ChangePasswordBody,
  CorrectCategorisationResponse,
  CreateDateEventBody,
  CreatePaymentBody,
  DailyDeck,
  DashboardResponse,
  DateEvent,
  DateEventList,
  DevicesResponse,
  EmailConnectionsResponse,
  InsightList,
  InsightSummary,
  InsightSummaryPeriod,
  LoginBody,
  LoginResponse,
  MeResponse,
  RegisterBody,
  RegisterResponse,
  PaymentTransaction,
  PaymentTransactionList,
  RecurringPaymentList,
  ReviewItemList,
  RevokeAllButCurrentBody,
  StatementUploadResponse,
  UpdateDateEventBody,
  UpdatePaymentBody,
  UpdatePreferencesBody,
  UpdateRecurringBody,
  UpdateTimezoneBody,
  UpdateTimezoneResponse,
  UserPreferences,
} from '../types/index.js';
import {
  uploadWithProgress,
  type UploadProgress,
} from './uploadWithProgress.js';

export type ApiClientMode = 'cookie' | 'bearer';

export interface ApiClientOptions {
  /**
   * Origin + optional path prefix for the API. The web SPA passes '' (empty
   * string) when running same-origin in prod, or 'http://localhost:3000' if
   * the consumer wants to bypass the Vite proxy. Trailing slashes are
   * stripped.
   */
  readonly baseURL: string;
  readonly mode: ApiClientMode;
  /**
   * Required even for cookie mode (use `NullTokenStore`). Bearer mode reads
   * the access token from `getAccessToken()` on every request.
   */
  readonly tokenStore: TokenStore;
  /**
   * Optional fetch override. Tests inject a mock; production uses the global
   * `fetch`.
   */
  readonly fetch?: typeof fetch;
}

interface RequestOptions {
  readonly method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly path: string;
  readonly query?: Record<string, string | number | boolean | undefined>;
  readonly body?: unknown;
}

export class ApiClient {
  private readonly baseURL: string;
  private readonly mode: ApiClientMode;
  private readonly tokenStore: TokenStore;
  private readonly fetchImpl: typeof fetch;

  public readonly auth: AuthMethods;
  public readonly dashboard: DashboardMethods;
  public readonly alerts: AlertsMethods;
  public readonly dailyDeck: DailyDeckMethods;
  public readonly payments: PaymentsMethods;
  public readonly dates: DatesMethods;
  public readonly usersMe: UsersMeMethods;

  constructor(options: ApiClientOptions) {
    this.baseURL = options.baseURL.replace(/\/+$/, '');
    this.mode = options.mode;
    this.tokenStore = options.tokenStore ?? new NullTokenStore();
    // Resolve fetch lazily on every request so test interceptors (MSW v2,
    // happy-dom shims, etc.) that REPLACE `globalThis.fetch` after this
    // module loads are honoured. Capturing a bound reference at construct
    // time would pin us to the unpatched implementation.
    this.fetchImpl =
      options.fetch ?? ((url, init) => globalThis.fetch(url, init));

    this.auth = new AuthMethods(this);
    this.dashboard = new DashboardMethods(this);
    this.alerts = new AlertsMethods(this);
    this.dailyDeck = new DailyDeckMethods(this);
    this.payments = new PaymentsMethods(this);
    this.dates = new DatesMethods(this);
    this.usersMe = new UsersMeMethods(this);
  }

  /** @internal — exposed to the per-namespace classes below. */
  public async request<T>(opts: RequestOptions): Promise<T> {
    const url = this.buildUrl(opts.path, opts.query);
    const headers: Record<string, string> = {
      Accept: 'application/json',
    };
    let body: BodyInit | undefined;
    if (opts.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(opts.body);
    }

    if (this.mode === 'bearer') {
      const token = await this.tokenStore.getAccessToken();
      if (token !== null && token !== undefined) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    const init: RequestInit = {
      method: opts.method,
      headers,
      ...(this.mode === 'cookie' ? { credentials: 'include' } : {}),
      ...(body !== undefined ? { body } : {}),
    };

    let response: Response;
    try {
      response = await this.fetchImpl(url, init);
    } catch (err) {
      throw new NetworkError(
        'Network request failed before reaching the server.',
        err,
      );
    }

    if (response.status === 204) {
      return undefined as T;
    }

    if (!response.ok) {
      const apiError = await decodeError(response);
      throw apiError;
    }

    // 2xx with body. We trust the server contract; the web layer can layer
    // a runtime zod parse on top if it wants belt-and-braces decoding.
    const text = await response.text();
    if (!text) {
      return undefined as T;
    }
    try {
      return JSON.parse(text) as T;
    } catch (err) {
      throw new ApiError({
        code: 'invalid_response',
        message: 'Server returned a non-JSON 2xx response.',
        status: response.status,
      });
    }
  }

  private buildUrl(
    path: string,
    query?: Record<string, string | number | boolean | undefined>,
  ): string {
    const url = `${this.baseURL}${path}`;
    if (query === undefined) return url;
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined) continue;
      params.append(k, String(v));
    }
    const qs = params.toString();
    return qs ? `${url}?${qs}` : url;
  }

  /** @internal */
  public getMode(): ApiClientMode {
    return this.mode;
  }

  /** @internal */
  public getTokenStore(): TokenStore {
    return this.tokenStore;
  }

  /** @internal — used by the upload helpers that bypass `request`. */
  public getBaseURL(): string {
    return this.baseURL;
  }
}

// ---- Helpers ----------------------------------------------------------------

/**
 * Generate a caller-supplied idempotency key (`event_id`). Browsers and
 * modern Node both expose `crypto.randomUUID`; the timestamp+random
 * fallback covers any environment that does not.
 */
function generateEventId(prefix: string): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c && typeof c.randomUUID === 'function') {
    return `${prefix}_${c.randomUUID()}`;
  }
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

// ---- Per-namespace method classes ------------------------------------------

class AuthMethods {
  constructor(private readonly client: ApiClient) {}

  /**
   * Phase 7.1 — POST /api/auth/register. 201 sets the session cookie
   * (cookie mode) and returns `{ user }` — same envelope as login. The
   * SPA can transition straight to the dashboard without a second
   * round-trip. Backend defaults `plan` to `'personal'` when omitted.
   *
   * 400 → `{ error: { code: 'invalid_body', fields: {...} } }`
   * 409 → `{ error: { code: 'email_already_registered' } }`
   */
  register(input: {
    email: string;
    password: string;
    plan?: 'free' | 'personal' | 'household';
  }): Promise<RegisterResponse> {
    const body: RegisterBody = {
      email: input.email,
      password: input.password,
      ...(input.plan !== undefined ? { plan: input.plan } : {}),
    };
    return this.client.request<RegisterResponse>({
      method: 'POST',
      path: endpoints.authRegister,
      body,
    });
  }

  login(body: LoginBody): Promise<LoginResponse> {
    return this.client.request<LoginResponse>({
      method: 'POST',
      path: endpoints.authLogin,
      body,
    });
  }

  logout(args: { refresh_token?: string } = {}): Promise<void> {
    const body = args.refresh_token !== undefined ? { refresh_token: args.refresh_token } : undefined;
    return this.client.request<void>({
      method: 'POST',
      path: endpoints.authLogout,
      ...(body !== undefined ? { body } : {}),
    });
  }

  me(): Promise<MeResponse> {
    return this.client.request<MeResponse>({
      method: 'GET',
      path: endpoints.authMe,
    });
  }

  /** Phase 7 T-A6: 204 on success; 401 on bad current password. */
  changePassword(body: ChangePasswordBody): Promise<void> {
    return this.client.request<void>({
      method: 'POST',
      path: endpoints.authChangePassword,
      body,
    });
  }

  /**
   * Phase 7 T-A4 / T-A6: 204 on success. Body must include the literal
   * confirmation phrase 'delete my account' AND the current password.
   * The cookie is cleared by the server; the client should drop any
   * cached tokens after this call resolves.
   */
  deleteAccount(body: AccountDeletionBody): Promise<void> {
    return this.client.request<void>({
      method: 'DELETE',
      path: endpoints.authDeleteAccount,
      body,
    });
  }

  /** Phase 7 D3: list devices (sessions + refresh tokens) for the user. */
  listDevices(): Promise<DevicesResponse> {
    return this.client.request<DevicesResponse>({
      method: 'GET',
      path: endpoints.authDevices,
    });
  }

  /** Phase 7 D3: revoke a single device by id. Idempotent 200 per Phase 5. */
  revokeDevice(id: string): Promise<{ ok: boolean }> {
    return this.client.request<{ ok: boolean }>({
      method: 'DELETE',
      path: endpointPaths.authDeviceItem(id),
    });
  }

  /** Phase 7 D3: revoke every other device. Cookie-mode callers omit body. */
  revokeAllButCurrent(body: RevokeAllButCurrentBody = {}): Promise<{ ok: boolean }> {
    const hasBody = body.refresh_token !== undefined;
    return this.client.request<{ ok: boolean }>({
      method: 'POST',
      path: endpoints.authDevicesAllButCurrent,
      ...(hasBody ? { body } : { body: {} }),
    });
  }
}

class DashboardMethods {
  constructor(private readonly client: ApiClient) {}

  list(args: { limit?: number } = {}): Promise<DashboardResponse> {
    return this.client.request<DashboardResponse>({
      method: 'GET',
      path: endpoints.dashboard,
      ...(args.limit !== undefined ? { query: { limit: args.limit } } : {}),
    });
  }
}

class AlertsMethods {
  constructor(private readonly client: ApiClient) {}

  list(
    args: { priority?: 'critical' | 'important' | 'info' | 'all'; limit?: number } = {},
  ): Promise<readonly AlertWithPinned[]> {
    const query: Record<string, string | number> = {};
    if (args.priority !== undefined) query['priority'] = args.priority;
    if (args.limit !== undefined) query['limit'] = args.limit;
    return this.client.request<readonly AlertWithPinned[]>({
      method: 'GET',
      path: endpoints.alerts,
      ...(Object.keys(query).length > 0 ? { query } : {}),
    });
  }
}

class DailyDeckMethods {
  constructor(private readonly client: ApiClient) {}

  today(): Promise<DailyDeck> {
    return this.client.request<DailyDeck>({
      method: 'GET',
      path: endpoints.dailyDeck,
    });
  }
}

class PaymentsMethods {
  public readonly reviewQueue: PaymentsReviewQueueMethods;
  public readonly recurring: PaymentsRecurringMethods;
  public readonly categories: PaymentsCategoriesMethods;

  constructor(private readonly client: ApiClient) {
    this.reviewQueue = new PaymentsReviewQueueMethods(client);
    this.recurring = new PaymentsRecurringMethods(client);
    this.categories = new PaymentsCategoriesMethods(client);
  }

  /** Phase 7.5 D3 — list AI-narrative insights, most-recent first. */
  listInsights(): Promise<InsightList> {
    return this.client.request<InsightList>({
      method: 'GET',
      path: endpoints.paymentsInsights,
    });
  }

  /** Phase 7.5 D3 — analytics summary for the requested period. */
  getInsightsSummary(args: { period: InsightSummaryPeriod }): Promise<InsightSummary> {
    return this.client.request<InsightSummary>({
      method: 'GET',
      path: endpoints.paymentsInsightsSummary,
      query: { period: args.period },
    });
  }

  /** Phase 7.5 D3 — list detected anomalies for the current user. */
  listAnomalies(): Promise<AnomalyList> {
    return this.client.request<AnomalyList>({
      method: 'GET',
      path: endpoints.paymentsAnomalies,
    });
  }

  /**
   * The backend's manual-payments list route currently exposes a `paid`
   * filter only (`'true' | 'false'`). The date-range filter referenced in
   * plan §3 D-D4 is applied client-side in Phase 6 (the wire payload is
   * small) until the backend grows a server-side range filter.
   */
  listTransactions(
    args: { paid?: boolean; from?: string; to?: string } = {},
  ): Promise<PaymentTransactionList> {
    const query: Record<string, string> = {};
    if (args.paid !== undefined) query['paid'] = String(args.paid);
    return this.client.request<PaymentTransactionList>({
      method: 'GET',
      path: endpoints.paymentsList,
      ...(Object.keys(query).length > 0 ? { query } : {}),
    });
  }

  /** Phase 7 T-A6: create a manual payment. 201 on success. */
  create(body: CreatePaymentBody): Promise<PaymentTransaction> {
    return this.client.request<PaymentTransaction>({
      method: 'POST',
      path: endpoints.paymentsList,
      body,
    });
  }

  /** Phase 7 T-A6: patch an existing payment by id. 200 on success. */
  update(id: string, body: UpdatePaymentBody): Promise<PaymentTransaction> {
    return this.client.request<PaymentTransaction>({
      method: 'PATCH',
      path: endpointPaths.paymentsItem(id),
      body,
    });
  }

  /** Phase 7 T-A6: soft-delete a payment by id. 204 on success. */
  delete(id: string): Promise<void> {
    return this.client.request<void>({
      method: 'DELETE',
      path: endpointPaths.paymentsItem(id),
    });
  }

  /**
   * Phase 7 D4 — multipart statement upload with progress reporting.
   * The backend route is `POST /api/modules/payments/statements`. Accepts
   * a single file under the `file` field plus an `event_id` (idempotency
   * key). Returns the parsed `StatementUploadResponse` body on 201.
   *
   * Cookie-mode callers get session credentials attached automatically;
   * bearer-mode callers get the access token attached as a header.
   *
   * The `xhrFactory` option is for tests only — production callers omit it.
   */
  uploadStatement(args: {
    file: File;
    eventId: string;
    onProgress?: (progress: UploadProgress) => void;
    xhrFactory?: () => XMLHttpRequest;
  }): Promise<StatementUploadResponse> {
    const form = new FormData();
    form.append('file', args.file);
    form.append('event_id', args.eventId);

    return (async (): Promise<StatementUploadResponse> => {
      const url = `${this.client.getBaseURL()}${endpoints.paymentsStatements}`;
      const isCookie = this.client.getMode() === 'cookie';
      let authHeader: string | undefined;
      if (!isCookie) {
        const token = await this.client.getTokenStore().getAccessToken();
        if (token !== null && token !== undefined) {
          authHeader = `Bearer ${token}`;
        }
      }
      const opts: Parameters<typeof uploadWithProgress<StatementUploadResponse>>[0] = {
        url,
        method: 'POST',
        body: form,
        withCredentials: isCookie,
      };
      if (authHeader !== undefined) {
        (opts as { authHeader?: string }).authHeader = authHeader;
      }
      if (args.onProgress !== undefined) {
        (opts as { onProgress?: (p: UploadProgress) => void }).onProgress = args.onProgress;
      }
      if (args.xhrFactory !== undefined) {
        (opts as { xhrFactory?: () => XMLHttpRequest }).xhrFactory = args.xhrFactory;
      }
      return uploadWithProgress<StatementUploadResponse>(opts);
    })();
  }

  /** Phase 7 D3: list connected Gmail mailboxes. */
  emailStatus(): Promise<EmailConnectionsResponse> {
    return this.client.request<EmailConnectionsResponse>({
      method: 'GET',
      path: endpoints.paymentsEmailStatus,
    });
  }

  /** Phase 7 D3: revoke a specific Gmail connection by accountEmailHash. */
  emailDisconnect(args: { accountEmailHash: string }): Promise<void> {
    return this.client.request<void>({
      method: 'DELETE',
      path: endpoints.paymentsEmailConnect,
      query: { accountEmailHash: args.accountEmailHash },
    });
  }

  /** Trigger a Gmail inbox scan for the given connected mailbox. */
  emailScan(args: { accountEmailHash: string; eventId: string }): Promise<EmailScanResult> {
    return this.client.request<EmailScanResult>({
      method: 'POST',
      path: endpoints.paymentsEmailScan,
      body: { event_id: args.eventId, accountEmailHash: args.accountEmailHash },
    });
  }
}

export interface EmailScanResult {
  jobId: string;
  status: string;
  messagesListed: number;
  messagesProcessed: number;
  detectedItems: number;
  aiCalls: number;
}

/**
 * Phase 7 D5 — categorisation review queue. Backed by the Phase 3 routes:
 *   GET  /api/modules/payments/categorisations/review
 *   POST /api/modules/payments/categorisations/:predictionId/correct
 */
class PaymentsReviewQueueMethods {
  constructor(private readonly client: ApiClient) {}

  list(): Promise<ReviewItemList> {
    return this.client.request<ReviewItemList>({
      method: 'GET',
      path: endpoints.paymentsCategorisationsReview,
    });
  }

  /**
   * Correct a prediction by selecting the right category. The
   * idempotency key (`event_id`) is generated inside the method so each
   * call is naturally idempotent (matches the pattern used by the
   * statements upload helper in Phase 7 D4).
   */
  correct(args: {
    predictionId: string;
    categoryId: string;
  }): Promise<CorrectCategorisationResponse> {
    const eventId = generateEventId('cat');
    return this.client.request<CorrectCategorisationResponse>({
      method: 'POST',
      path: endpointPaths.paymentsCategorisationCorrect(args.predictionId),
      body: {
        event_id: eventId,
        category_id: args.categoryId,
      },
    });
  }
}

/**
 * Phase 7 D5 — recurring payments. Backed by the Phase 3 routes:
 *   GET   /api/modules/payments/recurring
 *   PATCH /api/modules/payments/recurring/:id
 *
 * The PATCH body must include at least one of `days_before_due` or
 * `archived` (backend `.refine`). The SPA sends one at a time; the typed
 * wrapper does not enforce that here — the server returns 400 if violated
 * and the SPA surfaces it inline.
 */
class PaymentsRecurringMethods {
  constructor(private readonly client: ApiClient) {}

  list(): Promise<RecurringPaymentList> {
    return this.client.request<RecurringPaymentList>({
      method: 'GET',
      path: endpoints.paymentsRecurring,
    });
  }

  update(id: string, body: UpdateRecurringBody): Promise<unknown> {
    return this.client.request<unknown>({
      method: 'PATCH',
      path: endpointPaths.paymentsRecurringItem(id),
      body,
    });
  }
}

/**
 * Phase 7 D5 — per-user categories. Backed by the Phase 3 route:
 *   GET /api/modules/payments/categories
 *
 * Categories are seeded per-user on first call to the categorisation
 * service. The SPA fetches the list to render the correction Combobox
 * options in the review queue (the IDs vary per user, so a static slug
 * list is unusable for the wire shape `{ category_id }`).
 */
class PaymentsCategoriesMethods {
  constructor(private readonly client: ApiClient) {}

  list(): Promise<CategoryList> {
    return this.client.request<CategoryList>({
      method: 'GET',
      path: endpoints.paymentsCategories,
    });
  }
}

class DatesMethods {
  constructor(private readonly client: ApiClient) {}

  listEvents(args: { from?: string; to?: string } = {}): Promise<DateEventList> {
    const query: Record<string, string> = {};
    if (args.from !== undefined) query['from'] = args.from;
    if (args.to !== undefined) query['to'] = args.to;
    return this.client.request<DateEventList>({
      method: 'GET',
      path: endpoints.datesList,
      ...(Object.keys(query).length > 0 ? { query } : {}),
    });
  }

  /** Phase 7 T-A6: create a date event. 201 on success. */
  create(body: CreateDateEventBody): Promise<DateEvent> {
    return this.client.request<DateEvent>({
      method: 'POST',
      path: endpoints.datesList,
      body,
    });
  }

  /** Phase 7 T-A6: patch a date event by id. 200 on success. */
  update(id: string, body: UpdateDateEventBody): Promise<DateEvent> {
    return this.client.request<DateEvent>({
      method: 'PATCH',
      path: endpointPaths.datesItem(id),
      body,
    });
  }

  /** Phase 7 T-A6: delete a date event by id. 204 on success. */
  delete(id: string): Promise<void> {
    return this.client.request<void>({
      method: 'DELETE',
      path: endpointPaths.datesItem(id),
    });
  }
}

/**
 * Phase 7 T-A6: `/api/users/me/*` namespace — preferences + timezone CRUD
 * for the authenticated user.
 */
class UsersMeMethods {
  constructor(private readonly client: ApiClient) {}

  getPreferences(): Promise<UserPreferences> {
    return this.client.request<UserPreferences>({
      method: 'GET',
      path: endpoints.usersMePreferences,
    });
  }

  updatePreferences(body: UpdatePreferencesBody): Promise<UserPreferences> {
    return this.client.request<UserPreferences>({
      method: 'PATCH',
      path: endpoints.usersMePreferences,
      body,
    });
  }

  updateTimezone(body: UpdateTimezoneBody): Promise<UpdateTimezoneResponse> {
    return this.client.request<UpdateTimezoneResponse>({
      method: 'PATCH',
      path: endpoints.usersMeTimezone,
      body,
    });
  }
}
